import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, Trash2, Gavel, X, Pause } from "lucide-react";
import { toast } from "sonner";
import { fmtKES, photoUrl } from "@/lib/format";

export default function AuctionView() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [auction, setAuction] = useState(null);
  const [teams, setTeams] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [bidTeam, setBidTeam] = useState("");
  const [bidAmount, setBidAmount] = useState("");
  const [revealKey, setRevealKey] = useState(0);
  const lastPlayerIdRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    const [a, t, c] = await Promise.all([
      api.get("/auction"), api.get("/teams"), api.get("/config"),
    ]);
    setAuction(a.data);
    setTeams(t.data);
    setCfg(c.data);
    // Trigger reveal animation when current player changes
    if (a.data?.current_player_id && a.data.current_player_id !== lastPlayerIdRef.current) {
      lastPlayerIdRef.current = a.data.current_player_id;
      setRevealKey((k) => k + 1);
    }
  };
  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 1500);
    return () => clearInterval(pollRef.current);
  }, []);

  // For owner role — auto-pick their team
  useEffect(() => {
    if (user?.role === "owner" && user.team_id && !bidTeam) setBidTeam(user.team_id);
  }, [user]);

  const isAdmin = ["super_admin", "league_admin"].includes(user.role);
  const canBid = ["super_admin", "league_admin", "owner"].includes(user.role);

  const startAuction = async () => {
    try { await api.post("/auction/start", {}); toast.success("Auction live"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetAuction = async () => {
    if (!window.confirm("Reset full auction? All sales will be reverted.")) return;
    await api.post("/auction/reset");
    toast.success("Auction reset");
    load();
  };
  const placeBid = async () => {
    if (!bidTeam) return toast.error("Pick a team");
    const amount = Number(bidAmount);
    if (!amount) return toast.error("Enter amount");
    try {
      await api.post("/auction/bid", { team_id: bidTeam, amount });
      setBidAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const soldInFlightRef = useRef(false);
  const sold = async () => {
    // Client-side dedupe: block re-entry until the previous request has landed.
    // The backend also idempotency-guards via `idempotency_key`.
    if (soldInFlightRef.current) return;
    soldInFlightRef.current = true;
    const idempotencyKey = `${auction?.current_player_id || "?"}:${auction?.current_bid_team_id || "?"}:${auction?.current_bid_amount || 0}`;
    try {
      await api.post("/auction/sold", { idempotency_key: idempotencyKey });
      toast.success("SOLD!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTimeout(() => { soldInFlightRef.current = false; }, 600); }
  };
  const undoBid = async () => {
    if (!window.confirm("Undo the last bid? The previous highest bidder will be restored.")) return;
    try {
      await api.post("/auction/bid/undo", {});
      toast.success("Last bid reversed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const withdraw = async () => {
    try { await api.post("/auction/withdraw", {}); toast("Pushed to back of pool"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!auction) return <div className="p-8 text-zinc-500" data-testid="auction-loading">Loading…</div>;

  const p = auction.current_player;
  const minBid = (() => {
    if (!p || !cfg) return 0;
    const base = (cfg.category_min_bids || {})[p.category] || 0;
    return p.is_international ? Math.max(base, cfg.international_min_bid) : base;
  })();
  const requiredBid = Math.max(minBid, (auction.current_bid_amount || 0) + (auction.current_bid_team_id ? cfg?.bid_increment || 500 : 0));

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Module 2 · Theatre of Bids</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Live Auction</h1>
          <div className="mt-1 text-xs uppercase tracking-widest text-[#00A86B]">
            Status: {auction.status} · Pool remaining: {auction.pool_remaining_ids?.length || 0}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {auction.status !== "running" && (
              <Button data-testid="btn-auction-start" onClick={startAuction} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">
                <Gavel className="w-4 h-4 mr-1.5" /> Start Auction
              </Button>
            )}
            <Button data-testid="btn-auction-reset" onClick={resetAuction} variant="outline" className="border-red-500/40 text-red-400">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Reset
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Center stage */}
        <div className="lg:col-span-2">
          {p ? (
            <Card key={revealKey} className="bg-[#11241b] border-white/10 overflow-hidden slot-reveal" data-testid="auction-current-player">
              <div className="kenya-stripe" />
              <div className="px-4 py-1.5 bg-gradient-to-r from-[#006B3F]/30 via-transparent to-[#BB0000]/30 text-center text-[10px] uppercase tracking-[0.4em] text-white">
                ★ Random Pick · Player #{(auction.sales?.length || 0) + 1} of {(auction.sales?.length || 0) + (auction.pool_remaining_ids?.length || 0) + 1} ★
              </div>
              <div className="grid md:grid-cols-2">
                <div className="aspect-square bg-[#060d09] relative">
                  {p.photo_path ? (
                    <img src={photoUrl(p.photo_path)} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-700 font-anton text-9xl">
                      {p.name?.[0] || "?"}
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex flex-col gap-1">
                    <span className="px-2 py-1 bg-black/60 backdrop-blur text-[10px] uppercase tracking-widest text-[#00A86B]">CAT {p.category}</span>
                    {p.is_international && <span className="px-2 py-1 bg-amber-500 text-black text-[10px] uppercase tracking-widest font-bold">International</span>}
                    {!p.registration_fee_paid && <span className="px-2 py-1 bg-red-600 text-white text-[10px] uppercase tracking-widest font-bold">Fee Unpaid</span>}
                  </div>
                </div>
                <div className="p-6 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{p.country}</div>
                  <div className="font-anton text-4xl uppercase tracking-tight text-white mt-1 leading-none">{p.name}</div>
                  <div className="mt-2 text-sm text-zinc-400">Rating <span className="text-white font-bold">{p.rating}</span> · Seed {p.seed_level} · Tier {p.tier?.toUpperCase()}</div>
                  {p.achievements && (
                    <div className="mt-3 text-xs">
                      <div className="uppercase tracking-widest text-zinc-500 mb-1">Achievements</div>
                      <div className="text-zinc-300 whitespace-pre-line">{p.achievements}</div>
                    </div>
                  )}
                  <div className="mt-auto pt-6">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Current Bid</div>
                    <div className="font-anton text-6xl tracking-tighter text-[#00A86B] leading-none" data-testid="auction-current-bid">
                      {fmtKES(auction.current_bid_amount)}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      {auction.current_bid_team_id ? (
                        <>Top bidder: <span className="text-white">{teams.find(t => t.id === auction.current_bid_team_id)?.name}</span></>
                      ) : (
                        <>Opening bid: {fmtKES(minBid)}</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Bid controls */}
              {canBid && auction.status === "running" && (
                <div className="border-t border-white/10 p-4 grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">Bidding Team {user.role === "owner" && "(locked to your team)"}</label>
                    <select data-testid="auction-bid-team" value={bidTeam} onChange={(e) => setBidTeam(e.target.value)} disabled={user.role === "owner"} className="w-full mt-1 bg-[#060d09] border border-white/10 rounded-md px-3 py-2 text-sm text-white disabled:opacity-70">
                      <option value="">Choose…</option>
                      {teams.filter((t) => user.role !== "owner" || t.id === user.team_id).map((t) => (
                        <option key={t.id} value={t.id} disabled={t.purse_remaining < requiredBid}>
                          {t.name} — {fmtKES(t.purse_remaining)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">Bid Amount (min {fmtKES(requiredBid)})</label>
                    <Input data-testid="auction-bid-amount" type="number" step="500" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} placeholder={String(requiredBid)} className="bg-[#060d09] border-white/10 text-white mt-1" />
                  </div>
                  <Button data-testid="btn-place-bid" onClick={placeBid} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold h-10">
                    <Gavel className="w-4 h-4 mr-1.5" /> Place Bid
                  </Button>
                </div>
              )}
              {isAdmin && auction.status === "running" && (
                <div className="border-t border-white/10 p-4 flex flex-wrap gap-2">
                  <Button data-testid="btn-sold" disabled={!auction.current_bid_team_id || soldInFlightRef.current} onClick={sold} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase disabled:opacity-40">
                    SOLD! ({fmtKES(auction.current_bid_amount)})
                  </Button>
                  <Button data-testid="btn-undo-bid" disabled={!auction.history?.length} onClick={undoBid} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40">
                    ↶ Undo Last Bid
                  </Button>
                  <Button data-testid="btn-withdraw" onClick={withdraw} variant="outline" className="border-white/20 text-white">
                    <Pause className="w-4 h-4 mr-1.5" /> Unsold (move to back)
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-12 text-center bg-[#11241b] border-white/10" data-testid="auction-no-player">
              <Gavel className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <div className="font-anton text-2xl uppercase text-white">
                {auction.status === "completed" ? "Auction Completed" : auction.status === "idle" ? "Auction Not Started" : "No Active Player"}
              </div>
              {isAdmin && auction.status !== "running" && (
                <Button data-testid="btn-auction-start-empty" onClick={startAuction} className="mt-4 bg-[#00A86B] text-white">Start Auction</Button>
              )}
            </Card>
          )}
        </div>

        {/* Side: bid history + sales */}
        <div className="space-y-4">
          <Card className="p-4 bg-[#11241b] border-white/10">
            <div className="font-anton text-xl uppercase tracking-tight text-white mb-3">Bid Log</div>
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="auction-bid-history">
              {(auction.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-sm flex justify-between gap-2 text-zinc-300">
                  <span className="text-zinc-500 truncate">{h.team_name}</span>
                  <span className="text-[#00A86B] font-mono-score">{fmtKES(h.amount)}</span>
                </div>
              ))}
              {(auction.history || []).length === 0 && <div className="text-zinc-500 text-xs">No bids yet.</div>}
            </div>
          </Card>
          <Card className="p-4 bg-[#11241b] border-white/10">
            <div className="font-anton text-xl uppercase tracking-tight text-white mb-3">Recent Sales</div>
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="auction-sales">
              {(auction.sales || []).slice(-10).reverse().map((s, i) => {
                const tm = teams.find(t => t.id === s.team_id);
                return (
                  <div key={i} className="text-sm flex justify-between gap-2 text-zinc-300">
                    <span className="text-zinc-500 truncate">→ {tm?.name || "Team"}</span>
                    <span className="text-emerald-400 font-mono-score">{fmtKES(s.amount)}</span>
                  </div>
                );
              })}
              {(auction.sales || []).length === 0 && <div className="text-zinc-500 text-xs">No sales yet.</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
